import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import type { History, Location } from 'history';
import type * as Api from '@ViewModels';

/**
 * Return value from useDraftVersionState hook.
 */
interface UseDraftVersionStateReturn {
	/** Whether currently viewing the draft version */
	useDraft: boolean;
	/** Whether a draft version exists for this template */
	hasDraft: boolean;
	/** Whether a published version exists for this template */
	hasPublished: boolean;
	/** Function to toggle between draft and published versions */
	handleToggleDraft: (useDraft: boolean) => void;
}

/**
 * Initialize useDraft from URL query parameter.
 */
function initializeUseDraftFromUrl(locationSearch: string): boolean {
	const searchParams = new URLSearchParams(locationSearch);
	const usedraftParam = searchParams.get('usedraft');
	return usedraftParam === 'true';
}

/**
 * Check if template versions exist.
 * IAutomationTemplateReference is returned by the query, but can contain draft/published versions.
 */
function checkTemplateVersions(template: Api.IAutomationTemplateReference): {
	draftExists: boolean;
	publishedExists: boolean;
} {
	// Type assertion is safe here because the API returns an IAutomationTemplate structure.
	// Treat an empty version object as an existing version so brand-new manual automations
	// (no trigger, no steps yet) still open in draft and render their synthetic Manual trigger.
	const templateWithVersions = template as unknown as Api.IAutomationTemplate;
	const draftExists = templateWithVersions?.draft !== undefined && templateWithVersions?.draft !== null;
	const publishedExists = templateWithVersions?.published !== undefined && templateWithVersions?.published !== null;
	return { draftExists, publishedExists };
}

/**
 * Update URL with useDraft parameter.
 */
function updateUrlWithDraftParam(
	useDraft: boolean,
	locationSearch: string,
	history: History,
	location: Location,
	lastSetSearchRef: React.MutableRefObject<string | null>
): void {
	const searchParams = new URLSearchParams(locationSearch);
	if (useDraft) {
		searchParams.set('usedraft', 'true');
	} else {
		searchParams.delete('usedraft');
	}
	const newSearch = searchParams.toString();
	lastSetSearchRef.current = newSearch;
	history.replace({ ...location, search: newSearch ? `?${newSearch}` : '' });
}

/**
 * Manages draft/published version state with URL synchronization.
 *
 * This hook provides a way to toggle between viewing draft and published versions
 * of an automation template, with the state persisted in the URL query string
 * (`?usedraft=true`).
 *
 * Key behaviors:
 * - Initializes from URL query parameter on mount
 * - Auto-routes to draft on initial load when a draft exists
 * - Detects when a draft first appears (e.g., auto-save creates one) and switches to it
 * - Syncs state with URL changes (e.g., browser back/forward)
 * - Updates URL when user toggles between versions
 *
 * @param template - The automation template reference (may be undefined while loading)
 * @returns Object with draft state and toggle function
 *
 * @example
 * ```typescript
 * const { useDraft, hasDraft, hasPublished, handleToggleDraft } = useDraftVersionState(template);
 *
 * // Use in template expansion
 * const version = useDraft ? template.draft : template.published;
 *
 * // Render toggle UI
 * <button onClick={() => handleToggleDraft(false)} disabled={!hasPublished}>Published</button>
 * <button onClick={() => handleToggleDraft(true)} disabled={!hasDraft}>Draft</button>
 * ```
 */
export function useDraftVersionState(
	template: Api.IAutomationTemplateReference | undefined
): UseDraftVersionStateReturn {
	const history = useHistory();
	const location = useLocation();

	const [useDraft, setUseDraft] = useState(() => initializeUseDraftFromUrl(location.search));
	const [hasDraft, setHasDraft] = useState(false);
	const [hasPublished, setHasPublished] = useState(false);
	const lastSetSearchRef = useRef<string | null>(location.search.replace(/^\?/, ''));
	const initialSwitchDoneRef = useRef(false);
	const prevDraftExistsRef = useRef<boolean | null>(null);

	// Auto-route to draft on initial load, and detect when a draft first appears
	useEffect(() => {
		if (!template) {
			return;
		}

		const { draftExists, publishedExists } = checkTemplateVersions(template);
		setHasDraft(draftExists);
		setHasPublished(publishedExists);

		const searchParams = new URLSearchParams(location.search);
		const hasUsedraftParam = searchParams.has('usedraft');

		// Case 1: Initial page load - auto-route to draft if it exists
		//         (unless user explicitly set ?usedraft param in URL)
		if (!initialSwitchDoneRef.current) {
			initialSwitchDoneRef.current = true;
			if (draftExists && !useDraft && !hasUsedraftParam) {
				setUseDraft(true);
				updateUrlWithDraftParam(true, location.search, history, location, lastSetSearchRef);
			}
		}

		// Case 2: Draft just appeared (e.g., editing published triggered auto-save
		//         which created a draft) - switch to the new draft
		if (prevDraftExistsRef.current === false && draftExists && !useDraft) {
			setUseDraft(true);
			updateUrlWithDraftParam(true, location.search, history, location, lastSetSearchRef);
		}

		// Case 3: Draft was removed (e.g., user discarded changes) — switch back to published.
		// If there's no published version we leave useDraft as-is (Discard is disabled in that scenario).
		if (useDraft && !draftExists && publishedExists) {
			setUseDraft(false);
			updateUrlWithDraftParam(false, location.search, history, location, lastSetSearchRef);
		}

		prevDraftExistsRef.current = draftExists;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [template]);

	// Sync useDraft with URL parameter when URL changes externally
	useEffect(() => {
		const currentSearch = location.search.replace(/^\?/, '');
		if (lastSetSearchRef.current === currentSearch) {
			return;
		}

		const searchParams = new URLSearchParams(location.search);
		const usedraftParam = searchParams.get('usedraft');
		const newUseDraft = usedraftParam === 'true';
		if (newUseDraft !== useDraft) {
			setUseDraft(newUseDraft);
		}
		lastSetSearchRef.current = currentSearch;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.search]);

	const handleToggleDraft = useCallback(
		(newUseDraft: boolean) => {
			setUseDraft(newUseDraft);
			updateUrlWithDraftParam(newUseDraft, location.search, history, location, lastSetSearchRef);
		},
		[history, location]
	);

	return {
		useDraft,
		hasDraft,
		hasPublished,
		handleToggleDraft,
	};
}
